/**
 * useCreatePhoneCallNotes — persists a practitioner phone-call note.
 *
 * Faithful port of Flutter `dashboard_module`
 * (`dashboard_data_source_impl.dart` → `CreateUpdatePhoneCallNotes`,
 * `create_update_phone_call_notes_use_case.dart`,
 * `phone_call_notes_model.dart`). The Flutter data source POSTs the
 * PhoneCallNotesModel.toJson() shape to
 * `{patientAPI(PatientService.Patients)}/CreateUpdatePhoneCallNotes` and
 * returns the response body (the created/updated note id).
 *
 * Wire shape — EXACTLY the fields Flutter's `toJson()` serialises
 * (id · patientId · practitionerId · notes · notesPlainText · startDate);
 * no extra/invented fields. `startDate` is an ISO-8601 string
 * (Flutter `startDate?.toIso8601String()`).
 */
import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

/** The `CreateUpdatePhoneCallNotes` request body (Flutter `PhoneCallNotesModel.toJson`). */
export interface PhoneCallNotesRequest {
  /** Present only when editing an existing note; omit to create. */
  id?: string;
  patientId: string;
  practitionerId: string;
  /** Rich note payload — a Quill Delta JSON string (bare ops array). The PA
   *  client parses this with `tryParseDocument`, so it MUST be Delta, never bare
   *  plaintext (which renders blank). The caller encodes the TextArea text via
   *  `plainTextToDeltaJson`; the human-readable form rides `notesPlainText`. */
  notes: string;
  notesPlainText: string;
  /** ISO-8601. Flutter seeds `DateTime.now()` when the call is started. */
  startDate: string;
}

/**
 * POST /api/Patients/CreateUpdatePhoneCallNotes — create/update a phone-call
 * note. Returns the note id (the endpoint echoes the created row's id).
 */
export function useCreatePhoneCallNotes() {
  return useMutation({
    mutationFn: (body: PhoneCallNotesRequest) =>
      post<string>('/api/Patients/CreateUpdatePhoneCallNotes', body),
  });
}
